import { createFileRoute, Link } from "@tanstack/react-router"
import { format } from "date-fns"
import { ArrowLeft, Coins, FileText, Gavel, MessagesSquare } from "lucide-react"
import {
	DetailPanel,
	DetailPanelSection,
	PageHeader,
} from "@/components/layout"
import { Skeleton } from "@/components/ui/skeleton"
import { CouponCampaignConversation } from "@/features/coupon-campaigns-admin/CouponCampaignConversation"
import { CouponCampaignDecision } from "@/features/coupon-campaigns-admin/CouponCampaignDecision"
import {
	CouponCampaignMoneyBody,
	CouponCampaignRequestBody,
} from "@/features/coupon-campaigns-admin/CouponCampaignDetails"
import {
	CouponCampaignShopCard,
	CouponCampaignSiblingsCard,
} from "@/features/coupon-campaigns-admin/CouponCampaignShopCards"
import { CouponCampaignStatusBadge } from "@/features/coupon-campaigns-admin/CouponCampaignStatusBadge"
import { useCouponCampaign } from "@/queries/marketplace"

export const Route = createFileRoute("/coupon-requests/$campaignId")({
	component: CouponCampaignPage,
})

function BackLink() {
	return (
		<Link
			to="/coupon-requests"
			search={{} as never}
			className="inline-flex w-fit items-center gap-1 text-meta text-xs hover:text-foreground"
		>
			<ArrowLeft className="h-3.5 w-3.5" />
			Coupon requests
		</Link>
	)
}

/**
 * One coupon request: Request, Money, Conversation and Decision panels on the
 * left, the shop's context on the right (stacked below lg).
 */
function CouponCampaignPage() {
	const { campaignId } = Route.useParams()
	const { data: campaign, isLoading, error } = useCouponCampaign(campaignId)

	if (isLoading) {
		return (
			<div className="flex flex-col gap-4 p-4 sm:p-6">
				<Skeleton className="h-8 w-72 max-w-full" />
				<Skeleton className="h-64 w-full" />
			</div>
		)
	}

	if (error || !campaign) {
		return (
			<div className="flex flex-col gap-4 p-4 sm:p-6">
				<BackLink />
				<div className="rounded-lg bg-bad-soft px-4 py-3 text-bad">
					{error?.message ?? "Coupon request not found"}
				</div>
			</div>
		)
	}

	return (
		<div key={campaign.id} className="flex flex-col gap-6 p-4 sm:p-6">
			<div className="flex flex-col gap-3">
				<BackLink />
				<PageHeader
					title={
						<span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
							Coupon request from @{campaign.seller.username}
							<CouponCampaignStatusBadge
								campaign={campaign}
								className="font-normal text-sm"
							/>
						</span>
					}
					description={
						<>
							Submitted on{" "}
							<span className="tabular-nums">
								{format(
									new Date(campaign.createdAt),
									"MMM d, yyyy 'at' h:mm a",
								)}
							</span>{" "}
							· {campaign.name}
						</>
					}
				/>
			</div>

			<div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8">
				<div className="flex min-w-0 flex-col gap-4">
					<DetailPanel title="Request" icon={FileText}>
						<CouponCampaignRequestBody campaign={campaign} />
					</DetailPanel>

					<DetailPanel title="Money" icon={Coins}>
						<CouponCampaignMoneyBody campaign={campaign} />
					</DetailPanel>

					<DetailPanel
						title="Conversation"
						icon={MessagesSquare}
						badges={
							campaign.messages.length > 0 && (
								<span className="text-xs text-meta tabular-nums">
									{campaign.messages.length}
								</span>
							)
						}
					>
						<CouponCampaignConversation campaign={campaign} />
					</DetailPanel>

					<DetailPanel title="Decision" icon={Gavel}>
						<DetailPanelSection>
							<CouponCampaignDecision campaign={campaign} />
						</DetailPanelSection>
					</DetailPanel>
				</div>

				<div className="flex min-w-0 flex-col gap-4">
					<CouponCampaignShopCard campaign={campaign} />
					<CouponCampaignSiblingsCard campaign={campaign} />
				</div>
			</div>
		</div>
	)
}
